import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabase';
import Footer from '../components/Footer';
import './Schedule.css';

// ── helpers ──────────────────────────────────────────────────
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const TIMES = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00'];

function fmt12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
}
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function isToday(year, month, day) {
  const t = new Date();
  return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
}
function isPast(year, month, day) {
  const t = new Date(); t.setHours(0,0,0,0);
  return new Date(year, month, day) < t;
}

// ── TUTOR DASHBOARD ───────────────────────────────────────────
function TutorDashboard({ user, profile, showToast }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [mySlots, setMySlots] = useState([]);   // {date_str: [slots]}
  const [upcoming, setUpcoming] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [addingTime, setAddingTime] = useState(false);
  const [meetingLink, setMeetingLink] = useState(profile?.meeting_link || '');
  const [savingLink, setSavingLink] = useState(false);

  const saveMeetingLink = async () => {
    setSavingLink(true);
    const { error } = await supabase
      .from('profiles')
      .update({ meeting_link: meetingLink.trim() || null })
      .eq('id', user.id);
    setSavingLink(false);
    if (error) showToast('Could not save link: ' + error.message, 'error');
    else showToast('Meeting link saved!', 'success');
  };

  const fetchMySlots = useCallback(async () => {
    setLoadingSlots(true);
    const start = toDateStr(viewYear, viewMonth, 1);
    const end = toDateStr(viewYear, viewMonth, getDaysInMonth(viewYear, viewMonth));
    const { data } = await supabase
      .from('tutor_availability')
      .select('*')
      .eq('tutor_id', user.id)
      .gte('slot_date', start)
      .lte('slot_date', end)
      .order('slot_date').order('slot_time');
    const map = {};
    (data || []).forEach(s => {
      if (!map[s.slot_date]) map[s.slot_date] = [];
      map[s.slot_date].push(s);
    });
    setMySlots(map);
    setLoadingSlots(false);
  }, [user.id, viewYear, viewMonth]);

  const fetchUpcoming = useCallback(async () => {
    const todayStr = toDateStr(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const { data, error } = await supabase
      .from('lesson_bookings')
      .select('id, student_id, tutor_id, availability_id, slot_date, slot_time, duration_mins, status')
      .eq('tutor_id', user.id)
      .eq('status', 'confirmed')
      .gte('slot_date', todayStr)
      .order('slot_date').order('slot_time');
    if (error) { console.error('fetchUpcoming tutor error:', error); setUpcoming([]); return; }
    const bookings = data || [];
    if (bookings.length > 0) {
      const studentIds = [...new Set(bookings.map(b => b.student_id))];
      const { data: studentProfiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', studentIds);
      const studentMap = {};
      (studentProfiles || []).forEach(p => { studentMap[p.id] = p; });
      setUpcoming(bookings.map(b => ({ ...b, student_profile: studentMap[b.student_id] || null })));
    } else {
      setUpcoming([]);
    }
  }, [user.id]);

  useEffect(() => { fetchMySlots(); }, [fetchMySlots]);
  useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);

  const addSlot = async (time) => {
    if (!selectedDate) return;
    setAddingTime(true);
    const { error } = await supabase.from('tutor_availability').insert({
      tutor_id: user.id,
      slot_date: selectedDate,
      slot_time: time + ':00',
      duration_mins: 60,
    });
    if (error) {
      if (error.code === '23505') showToast('That time slot already exists', 'error');
      else showToast('Error adding slot: ' + error.message, 'error');
    } else {
      showToast('Slot added!', 'success');
      fetchMySlots();
    }
    setAddingTime(false);
  };

  const removeSlot = async (slot) => {
    if (slot.is_booked) { showToast("Can't remove a booked slot", 'error'); return; }
    const { error } = await supabase.from('tutor_availability').delete().eq('id', slot.id);
    if (!error) { showToast('Slot removed', 'success'); fetchMySlots(); }
  };

  const cancelBooking = async (b) => {
    await supabase.from('lesson_bookings').update({ status: 'cancelled' }).eq('id', b.id);
    await supabase.from('tutor_availability').update({ is_booked: false }).eq('id', b.availability_id);
    showToast('Lesson cancelled', 'success');
    fetchUpcoming();
    fetchMySlots();
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const slotsOnSelected = selectedDate ? (mySlots[selectedDate] || []) : [];
  const existingTimes = new Set(slotsOnSelected.map(s => s.slot_time.slice(0,5)));

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y=>y-1); setViewMonth(11); } else setViewMonth(m=>m-1); setSelectedDate(null); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y=>y+1); setViewMonth(0); } else setViewMonth(m=>m+1); setSelectedDate(null); };

  return (
    <div className="schedule-layout">
      <div className="schedule-main">
        <div className="schedule-header">
          <div className="section-tag">Tutor Dashboard</div>
          <h1 className="schedule-title">My <em>Availability</em></h1>
          <p className="schedule-subtitle">Set the dates and times you're available to teach in the next month. Students will be able to book from your open slots.</p>
        </div>

        {/* Calendar */}
        <div className="cal-wrap">
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <h2 className="cal-month">{MONTHS[viewMonth]} {viewYear}</h2>
            <button className="cal-nav-btn" onClick={nextMonth}>›</button>
          </div>
          <div className="cal-grid">
            {DAYS.map(d => <div key={d} className="cal-day-label">{d}</div>)}
            {[...Array(firstDay)].map((_, i) => <div key={'e'+i} className="cal-cell cal-cell--empty" />)}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dateStr = toDateStr(viewYear, viewMonth, day);
              const hasSlotsHere = !!(mySlots[dateStr] && mySlots[dateStr].length);
              const bookedHere = hasSlotsHere && mySlots[dateStr].some(s => s.is_booked);
              const past = isPast(viewYear, viewMonth, day);
              const selected = selectedDate === dateStr;
              return (
                <div
                  key={day}
                  className={[
                    'cal-cell',
                    past ? 'cal-cell--past' : '',
                    hasSlotsHere ? 'cal-cell--has-slots' : '',
                    bookedHere ? 'cal-cell--booked' : '',
                    isToday(viewYear, viewMonth, day) ? 'cal-cell--today' : '',
                    selected ? 'cal-cell--selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !past && setSelectedDate(dateStr)}
                >
                  <span className="cal-day-num">{day}</span>
                  {hasSlotsHere && (
                    <span className="cal-dot-row">
                      {mySlots[dateStr].slice(0, 3).map((s, si) => (
                        <span key={si} className={`cal-dot ${s.is_booked ? 'cal-dot--booked' : ''}`} />
                      ))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="cal-legend">
            <span><span className="cal-dot" />Available</span>
            <span><span className="cal-dot cal-dot--booked" />Booked</span>
          </div>
        </div>

        {/* Slot editor */}
        {selectedDate && (
          <div className="slot-editor">
            <h3 className="slot-editor-title">
              {fmtDate(selectedDate)}
              <span className="slot-count">{slotsOnSelected.length} slot{slotsOnSelected.length !== 1 ? 's' : ''}</span>
            </h3>

            {/* Existing slots */}
            {slotsOnSelected.length > 0 && (
              <div className="existing-slots">
                {slotsOnSelected.map(s => (
                  <div key={s.id} className={`slot-chip ${s.is_booked ? 'slot-chip--booked' : ''}`}>
                    <span>{fmt12(s.slot_time.slice(0,5))}</span>
                    <span className="slot-chip-status">{s.is_booked ? '📚 Booked' : '✓ Open'}</span>
                    {!s.is_booked && (
                      <button className="slot-remove-btn" onClick={() => removeSlot(s)} title="Remove">×</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add times */}
            <div className="add-slots-section">
              <p className="add-slots-label">Add time slots:</p>
              <div className="time-picker-grid">
                {TIMES.map(t => {
                  const alreadyAdded = existingTimes.has(t);
                  return (
                    <button
                      key={t}
                      className={`time-btn ${alreadyAdded ? 'time-btn--added' : ''}`}
                      disabled={alreadyAdded || addingTime}
                      onClick={() => addSlot(t)}
                    >
                      {fmt12(t)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!selectedDate && (
          <div className="select-date-prompt">
            <div className="select-date-icon">📅</div>
            <p>Click a date on the calendar to manage your availability for that day.</p>
          </div>
        )}

        {/* Meeting link editor */}
        <div className="meeting-link-editor">
          <div className="meeting-link-header">
            <span className="meeting-link-title">Your Meeting Link</span>
            <span className="meeting-link-badge">Optional</span>
          </div>
          <p className="meeting-link-desc">Add your Zoom, Google Meet, or any video call link. It will show up in your students' upcoming lessons so they can join easily.</p>
          <div className="meeting-link-row">
            <input
              type="url"
              className="meeting-link-input"
              placeholder="https://zoom.us/j/... or meet.google.com/..."
              value={meetingLink}
              onChange={e => setMeetingLink(e.target.value)}
            />
            <button
              className="meeting-link-save-btn"
              onClick={saveMeetingLink}
              disabled={savingLink}
            >
              {savingLink ? '...' : 'Save'}
            </button>
          </div>
          {profile?.meeting_link && (
            <a href={profile.meeting_link} target="_blank" rel="noopener noreferrer" className="meeting-link-preview">
              🔗 {profile.meeting_link.replace(/^https?:\/\//, '').slice(0, 40)}{profile.meeting_link.length > 46 ? '...' : ''}
            </a>
          )}
        </div>
      </div>

      {/* Sidebar: upcoming lessons */}
      <div className="schedule-sidebar">
        <h2 className="sidebar-title">Upcoming Lessons</h2>
        {upcoming.length === 0 ? (
          <div className="no-upcoming">
            <div className="no-upcoming-icon">🎵</div>
            <p>No upcoming lessons yet.<br />Students will book from your open slots.</p>
          </div>
        ) : (
          <div className="upcoming-list">
            {upcoming.map(b => (
              <div key={b.id} className="upcoming-card">
                <div className="upcoming-date-badge">
                  <span className="upcoming-month">{new Date(b.slot_date+'T00:00:00').toLocaleString('en-US',{month:'short'})}</span>
                  <span className="upcoming-day">{new Date(b.slot_date+'T00:00:00').getDate()}</span>
                </div>
                <div className="upcoming-info">
                  <div className="upcoming-student">
                    {b.student_profile?.full_name || 'Student'}
                  </div>
                  <div className="upcoming-time">{fmt12(b.slot_time.slice(0,5))} · {b.duration_mins} min</div>
                  {profile?.meeting_link && (
                    <a href={profile.meeting_link} target="_blank" rel="noopener noreferrer" className="join-meeting-btn">
                      🔗 Join Meeting
                    </a>
                  )}
                  <button className="upcoming-cancel-btn" onClick={() => cancelBooking(b)}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STUDENT DASHBOARD ─────────────────────────────────────────
function StudentDashboard({ user, profile, showToast }) {
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [tutorSlots, setTutorSlots] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const now = new Date();
  const today = toDateStr(now.getFullYear(), now.getMonth(), now.getDate());

  const fetchTutors = useCallback(async () => {
    setLoadingTutors(true);
    // Get tutors who have available (not booked) future slots
    const { data: slots } = await supabase
      .from('tutor_availability')
      .select('tutor_id')
      .eq('is_booked', false)
      .gte('slot_date', today);
    const tutorIds = [...new Set((slots || []).map(s => s.tutor_id))];
    if (tutorIds.length === 0) { setTutors([]); setLoadingTutors(false); return; }
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, user_type, instrument, bio')
      .in('id', tutorIds)
      .eq('user_type', 'tutor');
    setTutors(profiles || []);
    setLoadingTutors(false);
  }, [today]);

  const fetchTutorSlots = useCallback(async (tutorId) => {
    setLoadingSlots(true);
    const { data } = await supabase
      .from('tutor_availability')
      .select('*')
      .eq('tutor_id', tutorId)
      .eq('is_booked', false)
      .gte('slot_date', today)
      .order('slot_date').order('slot_time')
      .limit(60);
    setTutorSlots(data || []);
    setLoadingSlots(false);
  }, [today]);

  const fetchUpcoming = useCallback(async () => {
    const { data, error } = await supabase
      .from('lesson_bookings')
      .select('id, student_id, tutor_id, availability_id, slot_date, slot_time, duration_mins, status')
      .eq('student_id', user.id)
      .eq('status', 'confirmed')
      .gte('slot_date', today)
      .order('slot_date').order('slot_time');
    if (error) { console.error('fetchUpcoming student error:', error); setUpcoming([]); return; }
    // Fetch tutor names separately to avoid FK join ambiguity
    const bookings = data || [];
    if (bookings.length > 0) {
      const tutorIds = [...new Set(bookings.map(b => b.tutor_id))];
      const { data: tutorProfiles } = await supabase
        .from('profiles')
        .select('id, full_name, meeting_link')
        .in('id', tutorIds);
      const tutorMap = {};
      (tutorProfiles || []).forEach(p => { tutorMap[p.id] = p; });
      setUpcoming(bookings.map(b => ({ ...b, tutor_profile: tutorMap[b.tutor_id] || null })));
    } else {
      setUpcoming([]);
    }
  }, [user.id, today]);

  useEffect(() => { fetchTutors(); fetchUpcoming(); }, [fetchTutors, fetchUpcoming]);

  const selectTutor = (tutor) => {
    setSelectedTutor(tutor);
    fetchTutorSlots(tutor.id);
  };

  const bookSlot = async (slot) => {
    setBooking(true);
    // Insert booking — the DB trigger auto-flips is_booked on tutor_availability
    const { error: bookErr } = await supabase.from('lesson_bookings').insert({
      student_id: user.id,
      tutor_id: slot.tutor_id,
      availability_id: slot.id,
      slot_date: slot.slot_date,
      slot_time: slot.slot_time,
      duration_mins: slot.duration_mins || 60,
    });
    if (bookErr) {
      console.error('Booking error:', bookErr);
      showToast('Booking failed: ' + bookErr.message, 'error');
      setBooking(false);
      return;
    }
    // Optimistically also update is_booked in case trigger is slow
    await supabase.from('tutor_availability').update({ is_booked: true }).eq('id', slot.id);
    showToast('Lesson booked! Check your upcoming lessons below.', 'success');
    setBooking(false);
    // Refresh both lists
    await fetchUpcoming();
    fetchTutorSlots(slot.tutor_id);
  };

  const cancelBooking = async (b) => {
    await supabase.from('lesson_bookings').update({ status: 'cancelled' }).eq('id', b.id);
    await supabase.from('tutor_availability').update({ is_booked: false }).eq('id', b.availability_id);
    showToast('Lesson cancelled', 'success');
    fetchUpcoming();
    if (selectedTutor?.id === b.tutor_id) fetchTutorSlots(b.tutor_id);
  };

  // Group tutor slots by date
  const slotsByDate = {};
  tutorSlots.forEach(s => {
    if (!slotsByDate[s.slot_date]) slotsByDate[s.slot_date] = [];
    slotsByDate[s.slot_date].push(s);
  });

  const initials = (name) => name ? name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2) : '?';

  return (
    <div className="schedule-layout">
      <div className="schedule-main">
        <div className="schedule-header">
          <div className="section-tag">Student Dashboard</div>
          <h1 className="schedule-title">Book a <em>Lesson</em></h1>
          <p className="schedule-subtitle">Browse available tutors and pick a time that works for you. All sessions are free and held online.</p>
        </div>

        {/* Tutor list */}
        {loadingTutors ? (
          <div className="loading-state"><div className="spinner" /><p>Finding available tutors...</p></div>
        ) : tutors.length === 0 ? (
          <div className="select-date-prompt">
            <div className="select-date-icon">😔</div>
            <p>No tutors have open slots right now.<br />Check back soon!</p>
          </div>
        ) : (
          <>
            <h3 className="tutors-heading">Available Tutors</h3>
            <div className="tutors-grid">
              {tutors.map(t => (
                <div
                  key={t.id}
                  className={`tutor-card ${selectedTutor?.id === t.id ? 'tutor-card--selected' : ''}`}
                  onClick={() => selectTutor(t)}
                >
                  <div className="tutor-avatar">{initials(t.full_name)}</div>
                  <div className="tutor-info">
                    <div className="tutor-name">{t.full_name}</div>
                    {t.instrument && <div className="tutor-instrument">{t.instrument}</div>}
                    {t.bio && <div className="tutor-bio">{t.bio}</div>}
                  </div>
                  <div className="tutor-arrow">→</div>
                </div>
              ))}
            </div>

            {/* Slot browser */}
            {selectedTutor && (
              <div className="slot-browser">
                <h3 className="slot-browser-title">
                  Available slots with <em>{selectedTutor.full_name}</em>
                </h3>
                {loadingSlots ? (
                  <div className="loading-state"><div className="spinner" /></div>
                ) : tutorSlots.length === 0 ? (
                  <p className="no-slots-msg">No open slots available right now.</p>
                ) : (
                  <div className="slot-date-groups">
                    {Object.entries(slotsByDate).map(([date, slots]) => (
                      <div key={date} className="slot-date-group">
                        <div className="slot-date-label">{fmtDate(date)}</div>
                        <div className="slot-times-row">
                          {slots.map(s => (
                            <button
                              key={s.id}
                              className="book-slot-btn"
                              disabled={booking}
                              onClick={() => bookSlot(s)}
                            >
                              {fmt12(s.slot_time.slice(0,5))}
                              <span className="book-slot-dur">{s.duration_mins || 60} min</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sidebar: upcoming lessons */}
      <div className="schedule-sidebar">
        <h2 className="sidebar-title">Upcoming Lessons</h2>
        {upcoming.length === 0 ? (
          <div className="no-upcoming">
            <div className="no-upcoming-icon">📚</div>
            <p>No upcoming lessons.<br />Book a session from the tutors on the left!</p>
          </div>
        ) : (
          <div className="upcoming-list">
            {upcoming.map(b => (
              <div key={b.id} className="upcoming-card">
                <div className="upcoming-date-badge">
                  <span className="upcoming-month">{new Date(b.slot_date+'T00:00:00').toLocaleString('en-US',{month:'short'})}</span>
                  <span className="upcoming-day">{new Date(b.slot_date+'T00:00:00').getDate()}</span>
                </div>
                <div className="upcoming-info">
                  <div className="upcoming-student">
                    with {b.tutor_profile?.full_name || 'Tutor'}
                  </div>
                  <div className="upcoming-time">{fmt12(b.slot_time.slice(0,5))} · {b.duration_mins} min</div>
                  {b.tutor_profile?.meeting_link && (
                    <a href={b.tutor_profile.meeting_link} target="_blank" rel="noopener noreferrer" className="join-meeting-btn">
                      🔗 Join Meeting
                    </a>
                  )}
                  <button className="upcoming-cancel-btn" onClick={() => cancelBooking(b)}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN SCHEDULE PAGE ────────────────────────────────────────
export default function Schedule({ navigate, showToast, onAuthClick }) {
  const { user, profile, isTutor, loading } = useAuth();

  if (loading) {
    return (
      <div className="schedule-page">
        <div className="schedule-loading"><div className="spinner" /><p>Loading...</p></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="schedule-page">
        <div className="schedule-gate">
          <div className="schedule-gate-icon">🎵</div>
          <h2>Sign in to access scheduling</h2>
          <p>Create a free student or tutor account to book lessons and manage your schedule.</p>
          <button className="btn-primary" onClick={onAuthClick}>Sign In / Sign Up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-page">
      <div className="schedule-page-hero">
        <div className="page-hero-ghost">Schedule</div>
        <div className="section-tag">{isTutor ? 'Tutor Portal' : 'Student Portal'}</div>
        <div className="schedule-user-badge">
          <div className="schedule-user-avatar">
            {(profile?.full_name || user.email || 'U').slice(0,1).toUpperCase()}
          </div>
          <div>
            <div className="schedule-user-name">{profile?.full_name || user.email}</div>
            <div className="schedule-user-type">{isTutor ? '🎓 Tutor Account' : '🎵 Student Account'}</div>
          </div>
        </div>
      </div>

      {isTutor
        ? <TutorDashboard user={user} profile={profile} showToast={showToast} />
        : <StudentDashboard user={user} profile={profile} showToast={showToast} />
      }
      <Footer navigate={navigate} />
    </div>
  );
}
